import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import classnames from 'classnames';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildRecommendedTrimPath,
  buildReviewPath,
  EditorialReviewEntities,
  EditorialReviewModel,
} from 'client/data/models/editorial-review';
import {
  buildTrimSummariesPath,
  InventoryCoreConfigurationsModel,
} from 'client/data/models/inventory-core-configurations';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VisitorEntities } from 'client/data/models/visitor';
import { CorePageParams, getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { TRIMS_COMPARATOR_HEADER_SRC_INLINE } from 'site-modules/comparator/constants/trims-comparator';
import { getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { CoreBreadcrumbs } from 'site-modules/shared/components/core-breadcrumbs/core-breadcrumbs';
import { TrimsCta } from 'site-modules/shared/components/core-page/trims/trims-cta';

import './trims-comparator-intro.scss';

const WE_RECOMMEND_TAGS = {
  true: { details: 'details', summary: 'summary' },
  false: { details: 'div', summary: 'div' },
};

function TrimsComparatorIntroUI({
  params,
  vehicle,
  visitorLocation = null,
  editorialReview = null,
  recommendedTrimSlug = null,
  title = null,
  hasLeadFormDealers = false,
  isMobile = false,
}) {
  const {
    model: { name: modelName },
    year,
  } = vehicle;
  const { isUsed } = params;
  const showLeadForm = !params.isDealerlessOem && hasLeadFormDealers;

  const { details: DetailsTag, summary: SummaryTag } = WE_RECOMMEND_TAGS[isMobile];

  return (
    <div className="trims-comparator-intro">
      <div
        style={{
          backgroundImage: `url('${TRIMS_COMPARATOR_HEADER_SRC_INLINE[isMobile]}')`,
          backgroundSize: '100% 100%',
        }}
      >
        <Container className="mb-1 pb-2_5 pb-md-3_5">
          <CoreBreadcrumbs
            className="ps-0 text-white mb-1_5"
            vehicle={vehicle}
            breadcrumbsParams={{ params, pageDisplayName: 'Trims', urlPath: getUrlPathFromParams(params, 'trims') }}
          />
          <h1 className="heading-2 text-white mb-0_5">{title}</h1>
        </Container>
      </div>

      <Container>
        {!!editorialReview?.trimFeaturesBody && (
          <Row className="mb-3_5">
            <Col xs={12} lg={8} className={classnames({ 'mb-2 mb-lg-0': isMobile || isUsed })}>
              {!isUsed && !!editorialReview?.weRecommend && (
                <Fragment>
                  <h2 className="heading-3 mb-1">
                    Which {year} {modelName} trim does Edmunds recommend?
                  </h2>
                  <ContentFragment classes="mt-1">{editorialReview.weRecommend}</ContentFragment>
                </Fragment>
              )}
              <h2 className="heading-3 mb-1">
                {year} {modelName} trim level differences
              </h2>
              {!isMobile && <ContentFragment>{editorialReview?.trimFeaturesBodySnippet}</ContentFragment>}
              <ContentCollapse
                minHeight="125px"
                btnClasses="px-0_5 py-0 size-16 text-blue-40"
                btnContainerClasses="py-0"
                trackingId="view_trim_features"
                justify="justify-content-start"
                textOpen="Read less"
                textClosed="Read more"
                btnTextClasses="fw-normal"
                classes="trim-features-collapse size-16"
                showBtnBelow
                scrollToDefault
                resetParams={{
                  offset: SCROLL_OFFSET,
                  duration: SCROLL_DURATION,
                }}
              >
                {isMobile && <ContentFragment>{editorialReview?.trimFeaturesBodySnippet}</ContentFragment>}
                <ContentFragment classes="size-16">{editorialReview?.trimFeaturesBody}</ContentFragment>
              </ContentCollapse>
            </Col>
            <Col xs={12} lg={4} className="pos-r">
              {isUsed && (
                <div className="we-recommend-card rounded-12 bg-cool-gray-90 px-1 py-1">
                  <DetailsTag className="rounded-8 bg-white px-1 py-1" open={isMobile ? true : undefined}>
                    <SummaryTag className="d-flex justify-content-between align-items-center rounded">
                      <h3 className="heading-4 mb-0">
                        Which {year} {modelName} trim does Edmunds recommend?
                      </h3>
                    </SummaryTag>
                    <ContentFragment classes="mt-1">{editorialReview.weRecommend}</ContentFragment>
                  </DetailsTag>
                </div>
              )}
              {!isMobile && !isUsed && (
                <div className="trim-summary-section rounded-12 bg-cool-gray-90 px-1 py-1">
                  <h2 className="heading-3 mb-1_5">Pricing</h2>
                  <div
                    className="intro-trims-content px-1 px-lg-0_5 px-xl-1 py-1 border-0 rounded-8 bg-white"
                    data-tracking-parent="edm-entry-new_model_core_trims-model-summary"
                  >
                    <TrimsCta
                      params={getParamsFromVehicle(vehicle)}
                      vehicle={vehicle}
                      recommendedTrimSlug={recommendedTrimSlug}
                      visitorLocation={visitorLocation}
                      showLeadForm={showLeadForm}
                      isDealerlessOem={params.isDealerlessOem}
                      isMobile={isMobile}
                    />
                  </div>
                </div>
              )}
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
}

TrimsComparatorIntroUI.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  visitorLocation: VisitorEntities.Location,
  editorialReview: EditorialReviewEntities.EditorialReview,
  recommendedTrimSlug: PropTypes.string,
  title: PropTypes.string,
  hasLeadFormDealers: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export const TrimsComparatorIntro = connectToModel(TrimsComparatorIntroUI, {
  editorialReview: bindToPath(({ vehicle }) => buildReviewPath(getParamsFromVehicle(vehicle)), EditorialReviewModel),
  recommendedTrimSlug: bindToPath(
    ({ params, isMobile }) => !isMobile && !params.isUsed && buildRecommendedTrimPath(params),
    EditorialReviewModel
  ),
  hasLeadFormDealers: bindToPath(
    ({ params, vehicle, isMobile }) =>
      !isMobile && !params.isUsed && buildTrimSummariesPath(getParamsFromVehicle(vehicle)),
    InventoryCoreConfigurationsModel,
    trims => !!get(trims, '[0].hasLeadFormDealers')
  ),
});
